class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onCheckoutClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onCheckoutClick(event) {
    const upsellArrow = event.target.closest('.cart-drawer__upsell-arrow');
    if (upsellArrow) {
      this.onUpsellArrowClick(event, upsellArrow);
      return;
    }

    const protectionButton = event.target.closest('.cart-drawer__protection-checkout');
    if (protectionButton) {
      this.onProtectionCheckout(event, protectionButton);
      return;
    }

    const checkoutWithoutProtection = event.target.closest('#CartDrawer-Checkout-Without-Protection');
    if (checkoutWithoutProtection) this.onCheckoutWithoutProtection(event, checkoutWithoutProtection);
  }

  onUpsellArrowClick(event, button) {
    event.preventDefault();

    const viewport = this.querySelector('.cart-drawer__upsell-viewport');
    if (!viewport) return;

    const items = viewport.querySelectorAll('.cart-drawer__upsell-product');
    if (items.length < 2) return;

    const direction = button.dataset.direction === 'previous' ? -1 : 1;
    const currentIndex = Math.round(viewport.scrollLeft / viewport.clientWidth);
    const nextIndex = (currentIndex + direction + items.length) % items.length;

    viewport.scrollBy({
      left: nextIndex * viewport.clientWidth - viewport.scrollLeft,
      behavior: 'smooth',
    });
  }

  onProtectionCheckout(event, button) {
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

    const variantId = button.dataset.protectionVariantId;
    if (!variantId || button.dataset.protectionInCart === 'true') {
      this.submitCheckout();
      return;
    }

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const config = fetchConfig('javascript');
    delete config.headers['Content-Type'];

    fetch(routes.cart_add_url, { ...config, body: formData })
      .catch(() => {})
      .finally(() => this.submitCheckout());
  }

  onCheckoutWithoutProtection(event, button) {
    const protectionLine = Number(button.dataset.removeProtectionLine || 0);
    if (!protectionLine || button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

    event.preventDefault();
    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');

    const body = JSON.stringify({
      line: protectionLine,
      quantity: 0,
    });

    fetch(routes.cart_change_url, { ...fetchConfig(), body })
      .catch(() => {})
      .finally(() => this.submitCheckout());
  }

  submitCheckout() {
    const form = document.getElementById('CartDrawer-Form');
    const checkoutButton = document.getElementById('CartDrawer-Checkout-Without-Protection');

    if (!form) {
      window.location.href = '/checkout';
      return;
    }

    if (form.requestSubmit && checkoutButton) {
      form.requestSubmit(checkoutButton);
      return;
    }

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Checkout';
    form.appendChild(checkoutInput);
    form.submit();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.error = this.querySelector('.cart-drawer__upsell-error');
    this.variantSelect = this.querySelector('.cart-drawer__upsell-select');

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    if (this.variantSelect) this.variantSelect.addEventListener('change', (event) => event.stopPropagation());
  }

  onSubmit(event) {
    event.preventDefault();

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      this.form.submit();
      return;
    }

    if (this.button) {
      this.button.setAttribute('aria-disabled', true);
      this.button.classList.add('loading');
    }
    if (this.error) {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    const formData = new FormData(this.form);
    formData.append(
      'sections',
      cartDrawer.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    delete config.headers['Content-Type'];

    fetch(routes.cart_add_url, { ...config, body: formData })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (this.error) {
            this.error.textContent = response.description;
            this.error.hidden = false;
          }
          return;
        }

        cartDrawer.renderContents(response);
      })
      .catch(() => {
        if (this.error) {
          this.error.textContent = window.cartStrings.error;
          this.error.hidden = false;
        }
      })
      .finally(() => {
        if (this.button) {
          this.button.removeAttribute('aria-disabled');
          this.button.classList.remove('loading');
        }
      });
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
